import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { BookSection } from "../../application/services/inspection-book-consolidator-service.js";
import { LINE_LIST_COLUMNS, type GeneratedLineList } from "../../domain/inspection-book/line-list.js";
import { router } from "../router/router.js";
import { CONTEXT_LOAD_STYLES, ContextLoadTracker } from "../context/context-load.js";

interface Option { id: string; label: string }

/**
 * BOOK DE INSPEÇÃO — CONSOLIDADOR DOCUMENTAL.
 *
 * Esta página não cria nada: só localiza, organiza e exibe os documentos já
 * existentes (RI, END, AVR, Minuta, anexos do Equipamento, PIT do
 * Prontuário/NR-13) vinculados ao contexto selecionado. Todo o trabalho de
 * filtragem/isolamento por Projeto, Unidade e Equipamento acontece no
 * InspectionBookConsolidatorService — a página só exibe o que ele devolve.
 */
class InspectionBookPage extends HTMLElement {
  /**
   * Tratamento único das leituras de contexto (política em
   * presentation/context/context-load.ts). Um erro de permissão deixa de
   * virar lista vazia e de produzir "nenhum cadastro" com o cadastro
   * existente na base.
   */
  private readonly ctxLoad = new ContextLoadTracker();

  private isLoadingFilters = true;
  private isLoadingBook = false;
  private loadError = "";

  private clients: Option[] = [];
  private projects: Array<Option & { clientId: string }> = [];
  private unitAreas: Array<Option & { projectId: string }> = [];
  private items: Array<Option & { contextTypeLabel: string }> = [];

  private selectedClientId = "";
  private selectedProjectId = "";
  private selectedUnitAreaId = "";
  private selectedEquipmentId = "";

  private book: any = null; // InspectionBookConsolidated — tipo genérico para não acoplar a UI ao módulo de aplicação.
  private isDownloadingLineList = false;
  private lineListDownloadError = "";
  private isDownloadingBook = false;
  private bookDownloadError = "";

  async connectedCallback() {
    this.render();
    await this.loadFilterOptions();
  }

  private get platform(): any {
    return (window as any).app?.platform;
  }

  private ctx(): any {
    const c = contextStore.get();
    return { activeClientId: this.selectedClientId || c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
  }

  private can(permission: string): boolean {
    return contextStore.hasPermission(permission as any);
  }

  private async loadFilterOptions() {
    this.isLoadingFilters = true;
    this.render();
    const p = this.platform;
    try {
      const clients: any[] = (await this.ctxLoad.load("Clientes", () => p?.adminService?.listAuthorizedClients(this.ctx()))) || [];
      this.clients = clients.map(c => ({ id: String(c.id), label: c.fantasyName || c.name || String(c.id) }));
      if (!this.selectedClientId && this.clients.length === 1) this.selectedClientId = this.clients[0]!.id;
      await this.loadProjects();
    } catch (e) {
      console.warn("[BOOK DE INSPEÇÃO] Não foi possível carregar Clientes", e);
    } finally {
      this.isLoadingFilters = false;
      this.render();
    }
  }

  private async loadProjects() {
    const p = this.platform;
    const projects: any[] = (await this.ctxLoad.load("Projetos", () => p?.projectService?.listProjects(this.selectedClientId || undefined))) || [];
    this.projects = projects.map(pr => ({ id: String(pr.id), label: [pr.code, pr.name].filter(Boolean).join(" - ") || String(pr.id), clientId: String(pr.clientId || "") }));
    await this.loadUnitAreas();
  }

  private async loadUnitAreas() {
    const p = this.platform;
    const filter: any = {};
    if (this.selectedClientId) filter.clientId = this.selectedClientId;
    if (this.selectedProjectId) filter.projectId = this.selectedProjectId;
    const units: any[] = (await this.ctxLoad.load("Unidades / Áreas", () => p?.projectService?.listUnitAreas(Object.keys(filter).length ? filter : undefined))) || [];
    this.unitAreas = units.map(u => ({ id: String(u.id), label: [u.code, u.name].filter(Boolean).join(" - ") || String(u.id), projectId: String(u.projectId || "") }));
    await this.loadItems();
  }

  private async loadItems() {
    const p = this.platform;
    const consolidator = p?.inspectionBookConsolidatorService;
    if (!consolidator) { this.items = []; return; }
    const rows = await consolidator.listItems(this.ctx(), { projectId: this.selectedProjectId || null, unitAreaId: this.selectedUnitAreaId || null }).catch(() => []);
    this.items = rows.map((r: any) => ({ id: r.id, label: `${r.tag || r.name} — ${r.name}`.trim(), contextTypeLabel: r.contextTypeLabel }));
  }

  private async loadBook() {
    if (!this.selectedEquipmentId) return;
    this.isLoadingBook = true;
    this.loadError = "";
    this.book = null;
    this.render();
    const p = this.platform;
    try {
      this.book = await p.inspectionBookConsolidatorService.loadBook(this.ctx(), this.selectedEquipmentId);
    } catch (e: any) {
      console.error("[BOOK DE INSPEÇÃO] Falha ao carregar", e);
      this.loadError = e?.safeMessage || e?.message || "Não foi possível carregar o Book de Inspeção deste item.";
    } finally {
      this.isLoadingBook = false;
      this.render();
    }
  }

  /**
   * BAIXAR BOOK — Dossiê completo do Book de Inspeção em PDF consolidado.
   * Suporta Book Preliminar/Incompleto com marca d'água e apêndice de pendências,
   * preservando a rastreabilidade e sem quebrar o gate de emissão oficial.
   */
  private async downloadBookPdf(): Promise<void> {
    if (!this.selectedEquipmentId || this.isDownloadingBook) return;
    this.isDownloadingBook = true;
    this.bookDownloadError = "";
    this.render();
    try {
      await import("../../infrastructure/documents/builders/index.js");
      const { triggerBrowserDownload } = await import("../../infrastructure/documents/document-engine.js");
      const { IS_BACKEND_MODE } = await import("../../app/browser-platform.js");
      const p = this.platform;
      const bytes = await p.inspectionBookConsolidatorService.downloadConsolidatedPdf(this.ctx(), this.selectedEquipmentId, {
        clientLabel: this.clients.find(c => c.id === this.selectedClientId)?.label,
        projectLabel: this.projects.find(pr => pr.id === this.selectedProjectId)?.label,
        unitAreaLabel: this.unitAreas.find(u => u.id === this.selectedUnitAreaId)?.label
      });
      if (IS_BACKEND_MODE && bytes instanceof Uint8Array) {
        const safeTag = String(this.selectedEquipmentId).replace(/[^A-Za-z0-9._-]+/g, "_");
        triggerBrowserDownload(bytes, `BOOK_${safeTag}.pdf`);
      }
    } catch (e: any) {
      console.error("[BOOK DE INSPEÇÃO] Falha ao baixar o Book de Inspeção", e);
      this.bookDownloadError = e?.safeMessage || e?.message || "Não foi possível gerar o PDF do Book de Inspeção.";
    } finally {
      this.isDownloadingBook = false;
      this.render();
    }
  }

  /**
   * BAIXAR PDF — Lista de Linhas independente: não exige gerar o Book
   * completo. Usa a mesma fonte (PipingSystemLineListService) e a mesma
   * tabela do Preview e do RI (comando complementar, itens 16/17/19).
   */
  private async downloadLineListPdf(): Promise<void> {
    if (!this.selectedEquipmentId || this.isDownloadingLineList) return;
    this.isDownloadingLineList = true;
    this.lineListDownloadError = "";
    this.render();
    try {
      await import("../../infrastructure/documents/builders/index.js");
      const { triggerBrowserDownload } = await import("../../infrastructure/documents/document-engine.js");
      const { IS_BACKEND_MODE } = await import("../../app/browser-platform.js");
      const p = this.platform;
      const bytes = await p.pipingSystemLineListService.downloadPdf(this.ctx(), this.selectedEquipmentId, {
        clientLabel: this.clients.find(c => c.id === this.selectedClientId)?.label,
        projectLabel: this.projects.find(pr => pr.id === this.selectedProjectId)?.label,
        unitAreaLabel: this.unitAreas.find(u => u.id === this.selectedUnitAreaId)?.label
      });
      if (IS_BACKEND_MODE && bytes instanceof Uint8Array) {
        const safeTag = String(this.selectedEquipmentId).replace(/[^A-Za-z0-9._-]+/g, "_");
        triggerBrowserDownload(bytes, `LISTA_DE_LINHAS_${safeTag}.pdf`);
      }
    } catch (e: any) {
      console.error("[BOOK DE INSPEÇÃO] Falha ao baixar a Lista de Linhas", e);
      this.lineListDownloadError = e?.safeMessage || e?.message || "Não foi possível gerar o PDF da Lista de Linhas.";
    } finally {
      this.isDownloadingLineList = false;
      this.render();
    }
  }

  // ------------------------------------------------------------------ render

  private render() {
    this.innerHTML = `
      ${this.ctxLoad.banner()}<style>${this.styles()}</style>
      <style>${CONTEXT_LOAD_STYLES}</style>
      <div class="bookp">
        <header class="bookp-head">
          <nav class="crumb"><span class="crumb-root">INSPECT IA</span><span class="crumb-sep">›</span><span class="crumb-current">BOOK DE INSPEÇÃO</span></nav>
          <h1 class="bookp-title">Book de Inspeção</h1>
          <p class="bookp-sub">Consolidação automática dos documentos técnicos vinculados ao contexto selecionado.</p>
        </header>

        ${this.renderFilters()}
        ${this.selectedEquipmentId ? this.renderBook() : this.renderEmptySelection()}
      </div>`;
    this.bind();
  }

  private renderFilters(): string {
    const projects = this.selectedClientId ? this.projects.filter(p => p.clientId === this.selectedClientId) : this.projects;
    const units = this.selectedProjectId ? this.unitAreas.filter(u => u.projectId === this.selectedProjectId) : this.unitAreas;
    return `
      <section class="filters" aria-label="Contexto do Book">
        <label class="filter">
          <span class="filter-label">Cliente</span>
          <select class="ctl" id="f-client" ${this.isLoadingFilters ? "disabled" : ""}>
            <option value="">Selecione…</option>
            ${this.clients.map(c => `<option value="${this.esc(c.id)}" ${this.selectedClientId === c.id ? "selected" : ""}>${this.esc(c.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">Projeto</span>
          <select class="ctl" id="f-project" ${this.isLoadingFilters ? "disabled" : ""}>
            <option value="">Selecione…</option>
            ${projects.map(p => `<option value="${this.esc(p.id)}" ${this.selectedProjectId === p.id ? "selected" : ""}>${this.esc(p.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter">
          <span class="filter-label">Unidade / Área</span>
          <select class="ctl" id="f-unit" ${this.isLoadingFilters ? "disabled" : ""}>
            <option value="">Selecione…</option>
            ${units.map(u => `<option value="${this.esc(u.id)}" ${this.selectedUnitAreaId === u.id ? "selected" : ""}>${this.esc(u.label)}</option>`).join("")}
          </select>
        </label>
        <label class="filter filter-item">
          <span class="filter-label">Item (Sistema / Tubulação / Equipamento)</span>
          <select class="ctl" id="f-item" ${this.isLoadingFilters ? "disabled" : ""}>
            <option value="">Selecione…</option>
            ${this.items.map(i => `<option value="${this.esc(i.id)}" ${this.selectedEquipmentId === i.id ? "selected" : ""}>${this.esc(i.label)} · ${this.esc(i.contextTypeLabel)}</option>`).join("")}
          </select>
        </label>
        <div class="filter-actions">
          <button class="btn btn-primary" id="btn-load" type="button" ${!this.selectedEquipmentId || this.isLoadingBook ? "disabled" : ""}>Carregar Book</button>
          <button class="btn btn-primary" id="btn-download-book-filter" type="button" ${!this.selectedEquipmentId || this.isLoadingBook || this.isDownloadingBook ? "disabled" : ""}>${this.isDownloadingBook ? "Gerando PDF…" : "BAIXAR BOOK"}</button>
        </div>
      </section>`;
  }

  private renderEmptySelection(): string {
    return this.emptyCard("Selecione o contexto acima", "Escolha Cliente, Projeto, Unidade/Área e o Item (Sistema de Tubulação, Tubulação/Linha ou Equipamento Estático) para carregar o Book de Inspeção.");
  }

  private renderBook(): string {
    if (this.isLoadingBook) {
      return `<div class="card sk"><div class="sk-line w40"></div><div class="sk-line w60 tall"></div><div class="sk-line w30"></div></div>`;
    }
    if (this.loadError) {
      return this.emptyCard("Não foi possível carregar o Book", this.loadError);
    }
    if (!this.book) return this.renderEmptySelection();

    const ctx = this.book.context;
    return `
      <section class="card context-card">
        <div class="context-card-head">
          <div class="context-grid">
            <div><span class="ctx-label">CLIENTE</span><span class="ctx-value">${this.esc(this.clients.find(c => c.id === this.selectedClientId)?.label || "—")}</span></div>
            <div><span class="ctx-label">PROJETO</span><span class="ctx-value">${this.esc(this.projects.find(p => p.id === this.selectedProjectId)?.label || "—")}</span></div>
            <div><span class="ctx-label">UNIDADE / ÁREA</span><span class="ctx-value">${this.esc(this.unitAreas.find(u => u.id === this.selectedUnitAreaId)?.label || "—")}</span></div>
            <div><span class="ctx-label">TIPO</span><span class="ctx-value">${this.esc(ctx.contextTypeLabel)}</span></div>
            <div><span class="ctx-label">TAG / SISTEMA</span><span class="ctx-value">${this.esc(ctx.equipmentTag || ctx.equipmentName || "—")}</span></div>
          </div>
          <div class="context-actions">
            <button class="btn btn-primary" id="btn-download-book-main" type="button" ${this.isDownloadingBook ? "disabled" : ""}>
              ${this.isDownloadingBook ? "Gerando PDF…" : "BAIXAR BOOK (PDF)"}
            </button>
          </div>
        </div>
        ${this.bookDownloadError ? `<div class="empty-desc" style="color: var(--ia-danger-fg, #f87171); margin-top:10px;">${this.esc(this.bookDownloadError)}</div>` : ""}
      </section>
      <div class="sections">
        ${this.book.sections.filter((s: BookSection) => s.applicable).map((s: BookSection, idx: number) => this.renderSection(s, idx + 1)).join("")}
        ${this.renderGeneratedLineList()}
      </div>`;
  }

  /**
   * LISTA DE LINHAS gerada automaticamente — o mesmo modelo, a mesma ordem e
   * as mesmas células que vão para o PDF (PREVIEW = PDF). Só aparece quando o
   * item selecionado é um Sistema de Tubulação.
   */
  private renderGeneratedLineList(): string {
    const list: GeneratedLineList | null = this.book?.generatedLineList ?? null;
    if (!list) return "";
    const head = `
      <div class="sec-head">
        <h2 class="sec-title">Lista de Linhas</h2>
        <div class="sec-head-right">
          <span class="sec-count">${list.totalLines === 0 ? "Gerada automaticamente" : `Gerada automaticamente · ${list.totalLines} ${list.totalLines === 1 ? "linha" : "linhas"}`}</span>
          <button class="btn btn-primary" id="btn-download-line-list" type="button" ${this.isDownloadingLineList ? "disabled" : ""}>${this.isDownloadingLineList ? "Gerando…" : "BAIXAR PDF"}</button>
        </div>
      </div>
      ${this.lineListDownloadError ? `<div class="empty-desc" style="color: var(--ia-danger-fg, #f87171); margin-bottom:10px;">${this.esc(this.lineListDownloadError)}</div>` : ""}`;

    if (list.totalLines === 0) {
      return `
        <section class="card sec">
          ${head}
          <div class="empty-inline">
            <div class="empty-title">SISTEMA SEM LINHAS CADASTRADAS</div>
            <div class="empty-desc">O Sistema de Tubulação selecionado não possui Linhas vinculadas no Cadastro de Equipamentos. Cadastre ou importe as Linhas antes de gerar a Lista de Linhas.</div>
            <div class="empty-desc"><strong>TOTAL DE LINHAS: 0</strong></div>
            <div class="empty-actions">
              <button class="btn btn-primary" id="btn-view-system" type="button">VER SISTEMA</button>
              <button class="btn" id="btn-cancel-line-list" type="button">CANCELAR</button>
            </div>
          </div>
        </section>`;
    }

    return `
      <section class="card sec">
        ${head}
        <div class="ll-meta">
          <span><strong>SISTEMA:</strong> ${this.esc(list.systemTag || "—")}</span>
          <span><strong>DESCRIÇÃO / SERVIÇO:</strong> ${this.esc(list.systemDescription || "—")}</span>
          <span><strong>DATA:</strong> ${this.esc(list.generatedAt.slice(0, 10))}</span>
          <span><strong>TOTAL DE LINHAS DO SISTEMA:</strong> ${list.totalLines}</span>
        </div>
        <div class="ll-scroll">
          <table class="doc-table ll-table">
            <thead><tr>${LINE_LIST_COLUMNS.map(c => `<th>${this.esc(c.label)}</th>`).join("")}</tr></thead>
            <tbody>
              ${list.lines.map(line => `<tr>${LINE_LIST_COLUMNS.map(c => `<td>${this.esc(line[c.key])}</td>`).join("")}</tr>`).join("")}
            </tbody>
          </table>
        </div>
        ${list.documentReferences.length > 0 ? `<div class="ll-refs"><strong>REFERÊNCIAS DOCUMENTAIS DO SISTEMA:</strong> ${list.documentReferences.map(r => this.esc(r)).join(" · ")}</div>` : ""}
      </section>`;
  }

  private renderSection(section: BookSection, order: number): string {
    const count = section.rows.length;
    if (!section.authorized) {
      return `<section class="card sec">
        <div class="sec-head"><h2 class="sec-title">${String(order).padStart(2, "0")}. ${this.esc(section.label)}</h2></div>
        ${this.emptyInline("Sem permissão para consultar esta origem.")}
      </section>`;
    }
    return `
      <section class="card sec">
        <div class="sec-head">
          <h2 class="sec-title">${String(order).padStart(2, "0")}. ${this.esc(section.label)}</h2>
          <span class="sec-count">${count} ${count === 1 ? "encontrado" : "encontrados"}</span>
        </div>
        ${count === 0 ? this.emptyInline("Nenhum documento vinculado.") : `
          <table class="doc-table">
            <thead><tr><th>Nº</th><th>Origem</th><th>Revisão</th><th>Data</th><th>Status</th><th></th></tr></thead>
            <tbody>
              ${section.rows.map((r: any) => `
                <tr>
                  <td>${this.esc(r.number)}${r.isDraft ? '<span class="badge-draft">RASCUNHO</span>' : ""}</td>
                  <td>${this.esc(r.originLabel)}</td>
                  <td>${this.esc(r.revision || "—")}</td>
                  <td>${this.esc(r.date || "—")}</td>
                  <td>${r.statusLabel ? `<span class="badge-status">${this.esc(r.statusLabel)}</span>` : "—"}</td>
                  <td class="doc-actions">
                    ${r.viewRoute ? `<a class="link-action" href="${this.esc(r.viewRoute)}" target="_blank" rel="noopener">VISUALIZAR</a>` : ""}
                    ${r.downloadRoute ? `<a class="link-action" href="${this.esc(r.downloadRoute)}" target="_blank" rel="noopener" download>BAIXAR</a>` : ""}
                  </td>
                </tr>`).join("")}
            </tbody>
          </table>`}
      </section>`;
  }

  private emptyCard(title: string, desc: string): string {
    return `<div class="card empty">${this.emptyInline(desc, title)}</div>`;
  }
  private emptyInline(desc: string, title?: string): string {
    return `<div class="empty-inline">${title ? `<div class="empty-title">${this.esc(title)}</div>` : ""}<div class="empty-desc">${this.esc(desc)}</div></div>`;
  }

  private bind() {
    const client = this.querySelector("#f-client") as HTMLSelectElement | null;
    client?.addEventListener("change", async () => {
      this.selectedClientId = client.value;
      this.selectedProjectId = ""; this.selectedUnitAreaId = ""; this.selectedEquipmentId = ""; this.book = null;
      await this.loadProjects();
      this.render();
    });
    const project = this.querySelector("#f-project") as HTMLSelectElement | null;
    project?.addEventListener("change", async () => {
      this.selectedProjectId = project.value;
      this.selectedUnitAreaId = ""; this.selectedEquipmentId = ""; this.book = null;
      await this.loadUnitAreas();
      this.render();
    });
    const unit = this.querySelector("#f-unit") as HTMLSelectElement | null;
    unit?.addEventListener("change", async () => {
      this.selectedUnitAreaId = unit.value;
      this.selectedEquipmentId = ""; this.book = null;
      await this.loadItems();
      this.render();
    });
    const item = this.querySelector("#f-item") as HTMLSelectElement | null;
    item?.addEventListener("change", () => { this.selectedEquipmentId = item.value; this.book = null; this.render(); });

    this.querySelector("#btn-load")?.addEventListener("click", () => { void this.loadBook(); });
    this.querySelector("#btn-download-book-filter")?.addEventListener("click", () => { void this.downloadBookPdf(); });
    this.querySelector("#btn-download-book-main")?.addEventListener("click", () => { void this.downloadBookPdf(); });
    this.querySelector("#btn-view-system")?.addEventListener("click", () => {
      if (this.selectedEquipmentId) router.navigate(`/equipment/${this.selectedEquipmentId}`);
    });
    this.querySelector("#btn-cancel-line-list")?.addEventListener("click", () => {
      this.selectedEquipmentId = ""; this.book = null; this.render();
    });
    this.querySelector("#btn-download-line-list")?.addEventListener("click", () => { void this.downloadLineListPdf(); });
  }

  private esc(value: unknown): string {
    return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  private styles(): string {
    return `
      .bookp { display: flex; flex-direction: column; gap: 16px; }
      .crumb { display: flex; align-items: center; gap: 8px; font-size: 0.7rem; letter-spacing: 0.08em; text-transform: uppercase; }
      .crumb-root { color: var(--ia-fg-soft, #9BA4AE); }
      .crumb-sep { color: var(--ia-fg-muted, rgba(255,255,255,0.45)); }
      .crumb-current { color: var(--ia-fg, #C8D2DC); font-weight: 600; }
      .bookp-title { margin: 6px 0 2px; font-size: clamp(1.4rem, 2.2vw, 1.9rem); font-weight: 600; color: var(--ia-fg, #E7EAED); }
      .bookp-sub { margin: 0; color: var(--ia-fg-muted, #8A939D); font-size: 0.875rem; }

      .filters { display: grid; grid-template-columns: 1fr 1fr 1fr 1.4fr auto; gap: 12px; align-items: end; padding: 14px; border: 1px solid var(--ia-line, rgba(255,255,255,0.08)); border-radius: 10px; background: var(--ia-bg-panel, #12161A); }
      .filter { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
      .filter-label { font-size: 0.68rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); }
      .ctl { width: 100%; padding: 9px 10px; border-radius: 7px; background: var(--ia-bg-inset, #0E1216); border: 1px solid var(--ia-line, rgba(255,255,255,0.10)); color: var(--ia-fg, #DCE2E8); font-size: 0.82rem; font-family: inherit; }
      .filter-actions { display: flex; gap: 8px; }
      .btn { padding: 9px 16px; border-radius: 8px; font-size: 0.78rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; cursor: pointer; border: 1px solid transparent; }
      .btn:disabled { opacity: 0.5; cursor: not-allowed; }
      .btn-primary { background: var(--ia-bg-raised, #2B3A47); color: var(--ia-fg, #E7EAED); border-color: var(--ia-line, #3A4C5C); }
      .btn-primary:hover:not(:disabled) { background: var(--ia-bg-raised, #334554); }

      .card { background: var(--ia-bg-panel, #12161A); border: 1px solid var(--ia-line, rgba(255,255,255,0.08)); border-radius: 10px; padding: 16px; }
      .context-card-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
      .context-card .context-grid { display: grid; grid-template-columns: repeat(5, minmax(0,1fr)); gap: 14px; flex: 1; min-width: 0; }
      .context-actions { display: flex; align-items: center; }
      .ctx-label { display: block; font-size: 0.62rem; letter-spacing: 0.08em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); }
      .ctx-value { display: block; margin-top: 4px; font-size: 0.88rem; color: var(--ia-fg, #E7EAED); font-weight: 600; }

      .sections { display: flex; flex-direction: column; gap: 12px; }
      .sec-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
      .sec-head-right { display: flex; align-items: center; gap: 12px; }
      .sec-title { margin: 0; font-size: 0.92rem; font-weight: 600; color: var(--ia-fg, #DCE2E8); }
      .sec-count { font-size: 0.72rem; color: var(--ia-fg-muted, #8A939D); }

      .doc-table { width: 100%; border-collapse: collapse; font-size: 0.8rem; }
      .doc-table th { text-align: left; font-size: 0.62rem; letter-spacing: 0.07em; text-transform: uppercase; color: var(--ia-fg-muted, #8A939D); padding: 6px 8px; border-bottom: 1px solid var(--ia-line, rgba(255,255,255,0.08)); }
      .doc-table td { padding: 8px; border-bottom: 1px solid var(--ia-line, rgba(255,255,255,0.05)); color: var(--ia-fg-soft, #C3CAD1); vertical-align: middle; }
      .badge-draft { margin-left: 8px; font-size: 0.6rem; letter-spacing: 0.06em; padding: 2px 6px; border-radius: 4px; background: rgba(178,148,85,0.18); color: var(--ia-warning-fg, #D8C08C); }
      .badge-status { font-size: 0.68rem; padding: 2px 8px; border-radius: 999px; background: rgba(255,255,255,0.06); color: var(--ia-fg-soft, #C3CAD1); }
      .doc-actions { display: flex; gap: 10px; white-space: nowrap; }
      .link-action { font-size: 0.68rem; font-weight: 600; letter-spacing: 0.05em; color: var(--ia-info-fg, #9BB4CC); text-decoration: none; }
      .link-action:hover { text-decoration: underline; }

      .ll-meta { display: flex; flex-wrap: wrap; gap: 6px 22px; margin-bottom: 10px; font-size: 0.74rem; color: var(--ia-fg-soft, #9BA4AE); }
      .ll-meta strong { color: var(--ia-fg-muted, #8A939D); font-weight: 600; letter-spacing: 0.05em; }
      .ll-scroll { overflow-x: auto; }
      .ll-table { min-width: 1500px; font-size: 0.72rem; table-layout: fixed; }
      /* PREVIEW = PDF (comando "autoajuste inteligente", item 23): quebra
         entre palavras, igual ao PDF — nunca uma única linha truncada nem
         corte no meio da palavra (sem overflow-wrap:anywhere/break-word). */
      .ll-table th, .ll-table td { white-space: normal; overflow-wrap: normal; word-break: normal; }
      .ll-refs { margin-top: 10px; font-size: 0.72rem; color: var(--ia-fg-soft, #9BA4AE); }
      .ll-refs strong { color: var(--ia-fg-muted, #8A939D); letter-spacing: 0.05em; }
      .empty-actions { display: flex; gap: 10px; margin-top: 10px; }

      .empty { display: flex; align-items: center; justify-content: center; min-height: 96px; }
      .empty-inline { display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; gap: 6px; padding: 18px 12px; color: var(--ia-fg-muted, #8A939D); }
      .empty-title { font-size: 0.85rem; color: var(--ia-fg-soft, #C3CAD1); font-weight: 600; }
      .empty-desc { font-size: 0.75rem; max-width: 420px; }

      .sk { min-height: 120px; display: flex; flex-direction: column; gap: 10px; justify-content: center; }
      .sk-line { height: 10px; border-radius: 4px; background: linear-gradient(90deg, rgba(255,255,255,0.05), rgba(255,255,255,0.10), rgba(255,255,255,0.05)); background-size: 200% 100%; animation: sk 1.2s ease-in-out infinite; }
      .sk-line.tall { height: 24px; }
      .w30 { width: 30%; } .w40 { width: 40%; } .w60 { width: 60%; }
      @keyframes sk { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

      @media (max-width: 1100px) {
        .filters { grid-template-columns: 1fr 1fr; }
        .filter-item { grid-column: 1 / -1; }
        .filter-actions { grid-column: 1 / -1; justify-content: flex-end; }
        .context-card .context-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
      }
      @media (max-width: 640px) {
        .filters { grid-template-columns: 1fr; }
        .context-card .context-grid { grid-template-columns: 1fr; }
        .doc-table { display: block; overflow-x: auto; }
      }`;
  }
}

if (!customElements.get("app-inspection-book-page")) {
  customElements.define("app-inspection-book-page", InspectionBookPage);
}
